import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Stack } from '@curves/ui-base';
import { useHistory } from 'react-router-dom';
import { MEMBER_REDEEM_HISTORY_ROUTER, MEMBER_REDEEM_PRODUCT_SELECT_ROUTER } from '@Router/routerPath';
import { useMainLayoutContext } from '@Local/Components/Layout/Context/MainLaydoutContext';
import { FOOTER_TYPE, HEADER_TYPE, THEME_TYPE } from '@Constants/dataEnums';
import { PointsSummaryBox, RedeemItemList } from '@Local/Redeem/Components';
import useRedeemCart from '@Local/Redeem/hooks/useRedeemCart';
import useMemberRedeemApi from '@Apis/useMemberRedeemApi';
import { parseDate } from '@Util/moment';

// 後端已選結果（陣列）轉為前端 cart map：{ [itemID]: quantity }
const toCartMap = (redeemResults = []) =>
    redeemResults.reduce((map, { itemID, quantity }) => {
        map[itemID] = quantity;
        return map;
    }, {});

const RedeemPage = () => {
    const { setHeaderType, setFooterType, setThemeType } = useMainLayoutContext();
    const history = useHistory();
    const { getRewardApi, getRedeemCartApi } = useMemberRedeemApi();

    const [reward, setReward] = useState(null);
    // 當期兌換活動的整體資訊（getRedeemCartApi 回應整包）
    const [redeemInfo, setRedeemInfo] = useState(null);

    // 由 redeemInfo 衍生的顯示值，避免一支回應拆成多個 state
    const hasActivity = !!redeemInfo?.hasActivity;
    const products = redeemInfo?.redeemCartItems ?? [];
    const canRedeem = !!redeemInfo?.canRedeem;
    const redeemStatus = redeemInfo?.redeemStatus ?? null;
    const redeemStatusText = redeemInfo?.redeemStatusText ?? '';
    // 兌換活動說明（目前進行中活動）
    const description = redeemInfo?.description ?? '';
    // 兌換期間（目前進行中活動的起迄日）
    const activityPeriod = useMemo(
        () =>
            redeemInfo?.redeemStart && redeemInfo?.redeemEnd
                ? {
                      startDate: parseDate(redeemInfo.redeemStart, 'YYYY/MM/DD'),
                      endDate: parseDate(redeemInfo.redeemEnd, 'YYYY/MM/DD'),
                  }
                : null,
        [redeemInfo],
    );

    // 可分配點數由後端 getRedeemCartApi 回傳，與 SaveRedeem 驗證上限同一條式子
    const redeemablePoints = redeemInfo?.redeemablePoints ?? 0;

    // 已確認的兌換清單；「目前點數」直接顯示 API 原值，後端已於兌換時扣點，前端不再重複扣除
    const { cart, hasCartItems, resetCart } = useRedeemCart(products, reward?.currentPoints ?? 0);

    useEffect(() => {
        setHeaderType(HEADER_TYPE.LOGO_WHITE | HEADER_TYPE.HIDE_BACKGROUND | HEADER_TYPE.UNFIXED);
        setFooterType(FOOTER_TYPE.HIDE);
        setThemeType(THEME_TYPE.DARK);
    }, [setHeaderType, setFooterType, setThemeType]);

    const loadData = useCallback(async () => {
        const [rewardResp, cartResp] = await Promise.all([getRewardApi(), getRedeemCartApi()]);
        if (rewardResp) setReward(rewardResp);
        if (cartResp) {
            setRedeemInfo(cartResp);
            resetCart(toCartMap(cartResp.redeemResults));
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // 導向兌換選品頁，帶入目前已確認的草稿與活動快照
    // canRedeem 一併帶下：活動未開始（false）時選品頁僅供預覽，送出類按鈕停用
    const handleGoToSelect = () =>
        history.push(MEMBER_REDEEM_PRODUCT_SELECT_ROUTER, {
            sourceCart: cart,
            products,
            redeemablePoints,
            currentPoints: reward?.currentPoints ?? 0,
            canRedeem,
            // 兌換期間一併帶下：選品頁在不可兌換時需據此判斷屬尚未開始或已結束
            redeemStart: redeemInfo?.redeemStart ?? null,
            redeemEnd: redeemInfo?.redeemEnd ?? null,
        });
    const handleGoToHistory = () => history.push(MEMBER_REDEEM_HISTORY_ROUTER);

    const expiryDate = reward?.expiryDate ? parseDate(reward.expiryDate, 'YYYY/MM/DD') : null;

    return (
        <Box className="redeem-page">
            <Box className="container">
                <PointsSummaryBox currentPoints={reward?.currentPoints ?? 0} expiryDate={expiryDate} />
                <RedeemItemList
                    cart={cart}
                    products={products}
                    hasActivity={hasActivity}
                    canRedeem={canRedeem}
                    description={description}
                    activityPeriod={activityPeriod}
                    redeemStatus={redeemStatus}
                    redeemStatusText={redeemStatusText}
                    onEdit={handleGoToSelect}
                />
                <Box className="redeem-action">
                    <Stack direction="column" spacing={1}>
                        <Button variant="contained" fullWidth disabled={!hasActivity} onClick={handleGoToSelect}>
                            {!canRedeem ? '查看兌換商品' : hasCartItems ? '修改兌換選擇' : '兌換商品'}
                        </Button>
                        <Button variant="text" fullWidth onClick={handleGoToHistory}>
                            查看紀錄
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};

export default RedeemPage;
